import { GetMasterDataPurchasersDTO } from "@src/domain/dto/masterData";
import type { GetMasterDataPurchasersUseCase } from "@src/application/useCases/masterData";
import {
  getMasterDataPurchasersLookupCache,
  getMasterDataPurchasersLookupEpoch,
  setMasterDataPurchasersLookupCache,
} from "./masterDataListCache";

export interface MasterDataPurchaserLookupOption {
  purchaserId: number;
  purchaserName: string;
  purchaserKey: string;
}

const inflightPurchasersByBrand = new Map<
  string,
  Promise<MasterDataPurchaserLookupOption[]>
>();

/** Active purchasers for the brand. One network call until the lookup cache is cleared. */
export function loadMasterDataPurchasersLookup(
  brandKey: string,
  getPurchasersUseCase: GetMasterDataPurchasersUseCase,
): Promise<MasterDataPurchaserLookupOption[]> {
  const cached =
    getMasterDataPurchasersLookupCache<MasterDataPurchaserLookupOption>(brandKey);
  if (cached) {
    return Promise.resolve(cached);
  }

  const inflight = inflightPurchasersByBrand.get(brandKey);
  if (inflight !== undefined) {
    return inflight;
  }

  const epoch = getMasterDataPurchasersLookupEpoch();
  const request = getPurchasersUseCase
    .execute(
      new GetMasterDataPurchasersDTO({
        search: null,
        includeInactive: false,
      }),
      brandKey,
    )
    .then((result) => {
      const options = result.items
        .filter((item) => item.isActive)
        .map((item) => ({
          purchaserId: item.purchaserId,
          purchaserName: item.purchaserName,
          purchaserKey: item.purchaserKey,
        }));
      if (epoch === getMasterDataPurchasersLookupEpoch()) {
        setMasterDataPurchasersLookupCache(brandKey, options);
      }
      return options;
    })
    .finally(() => {
      if (inflightPurchasersByBrand.get(brandKey) === request) {
        inflightPurchasersByBrand.delete(brandKey);
      }
    });

  inflightPurchasersByBrand.set(brandKey, request);
  return request;
}
